import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { loginSchema, type LoginFormData } from '../../lib/validators';
import { useAuth, redirectAfterLogin } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { AppSelector } from '../../components/shared/AppSelectorModal';
import { SessionConfirmModal } from '../../components/shared/SessionConfirmModal';
import { getErrorMessage, extractApiError } from '../../lib/errorCodes';
import { toast } from 'sonner';
import type { AppInfo } from '../../types/auth';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, expireAndRelogin } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showAppSelector, setShowAppSelector] = useState(false);
  const [selectedApps, setSelectedApps] = useState<AppInfo[]>([]);
  const [selectedToken, setSelectedToken] = useState('');

  const [showSessionModal, setShowSessionModal] = useState(false);
  const [pendingSessionToken, setPendingSessionToken] = useState('');
  const [isExpiring, setIsExpiring] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<LoginFormData | null>(null);
  const [formDisabled, setFormDisabled] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const handleSuccessLogin = (result: { apps?: AppInfo[]; access_token?: string }) => {
    if (result.apps && result.apps.length > 1) {
      setSelectedApps(result.apps);
      setSelectedToken(result.access_token || '');
      setShowAppSelector(true);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await login(data.username, data.password);

      if (result.error) {
        toast.error(getErrorMessage(result.errorCode, 'apiErrors.invalidCredentials'));
        return;
      }

      if (result.hasExistingSession) {
        setPendingSessionToken(result.sessionToken || '');
        setPendingCredentials(data);
        setFormDisabled(true);
        setShowSessionModal(true);
        return;
      }

      if (result.success && result.access_token) {
        if (result.apps && result.apps.length > 1) {
          handleSuccessLogin(result);
        } else {
          const redirected = redirectAfterLogin(result.access_token, result.apps);
          if (!redirected) {
            toast.error(t('apiErrors.noAppsAssigned'));
          }
        }
      }
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpireAndContinue = async () => {
    if (!pendingCredentials || !pendingSessionToken) return;
    setIsExpiring(true);
    try {
      const result = await expireAndRelogin(
        pendingSessionToken,
        pendingCredentials.username,
        pendingCredentials.password
      );

      if (result.error) {
        toast.error(getErrorMessage(result.errorCode, 'apiErrors.invalidCredentials'));
        setShowSessionModal(false);
        setFormDisabled(false);
        return;
      }

      setShowSessionModal(false);
      setFormDisabled(false);

      if (result.success && result.access_token) {
        if (result.apps && result.apps.length > 1) {
          handleSuccessLogin(result);
        } else {
          const redirected = redirectAfterLogin(result.access_token, result.apps);
          if (!redirected) {
            toast.error(t('apiErrors.noAppsAssigned'));
          }
        }
      }
    } catch (error) {
      toast.error(extractApiError(error));
      setShowSessionModal(false);
      setFormDisabled(false);
    } finally {
      setIsExpiring(false);
      setIsLoading(false);
    }
  };

  const handleCancelSession = () => {
    setShowSessionModal(false);
    setFormDisabled(false);
    setPendingSessionToken('');
    setPendingCredentials(null);
    setIsLoading(false);
  };

  return (
    <>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('general.welcomeTitle')}
        </h2>
        <p className="text-gray-600">{t('general.welcomeDesc')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={`space-y-5 transition-opacity ${formDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <Input
          label={t('general.emailLabel')}
          type="email"
          placeholder="votre.email@exemple.com"
          autoComplete="email"
          error={errors.username?.message}
          {...register('username')}
        />
        <PasswordInput
          label={t('general.passwordLabel')}
          placeholder="••••••••"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <Button type="submit" isLoading={isLoading} disabled={formDisabled}>
          {t('general.loginButton')}
        </Button>
      </form>

      <div className={`mt-4 text-center transition-opacity ${formDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="text-sm text-primary hover:text-secondary transition-colors"
        >
          {t('general.forgotPasswordLink')}
        </button>
      </div>

      {showSessionModal && (
        <SessionConfirmModal
          onContinue={handleExpireAndContinue}
          onCancel={handleCancelSession}
          isLoading={isExpiring}
        />
      )}

      {showAppSelector && (
        <AppSelector
          apps={selectedApps}
          token={selectedToken}
          onClose={() => setShowAppSelector(false)}
        />
      )}
    </>
  );
}
